import React, { useEffect } from 'react';
import { X, WifiOff, Zap, ArrowRightLeft, Download } from 'lucide-react';

/**
 * ExportBlockedModal
 *
 * Shows a beautiful modal when the user tries to export while in Socket (Stream) mode.
 * Reusable across all history pages — just pass `isOpen` and `onClose`.
 *
 * Props:
 *   isOpen   {boolean}  — controls visibility
 *   onClose  {fn}       — called when user dismisses
 *   feature  {string}   — optional label, e.g. "WhatsApp Export" (default: "Export")
 */
export default function ExportBlockedModal({ isOpen, onClose, feature = 'Export' }) {
    // Close on Escape key
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        /* Backdrop */
        <div
            className="fixed inset-0 z-[999] flex items-center justify-center p-4"
            style={{ background: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(8px)' }}
            onClick={onClose}
        >
            {/* Panel */}
            <div
                className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl border border-white/10"
                style={{ background: 'rgba(10, 18, 38, 0.95)' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Top gradient bar */}
                <div className="h-1 w-full bg-gradient-to-r from-orange-500 via-amber-400 to-yellow-500" />

                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-1.5 rounded-lg text-white/30 hover:text-white hover:bg-white/10 transition"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="p-8">
                    {/* Icon cluster */}
                    <div className="flex items-center justify-center mb-6">
                        <div className="relative">
                            {/* Outer glow ring */}
                            <div className="w-24 h-24 rounded-full bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                                {/* Inner circle */}
                                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500/30 to-amber-500/20 border border-orange-400/30 flex items-center justify-center">
                                    <Download className="w-7 h-7 text-orange-300" />
                                </div>
                            </div>

                            {/* Badge icon */}
                            <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-slate-900 border-2 border-orange-500/40 flex items-center justify-center">
                                <WifiOff className="w-4 h-4 text-orange-400" />
                            </div>
                        </div>
                    </div>

                    {/* Title */}
                    <h2 className="text-white text-xl font-bold text-center mb-2">
                        {feature} Unavailable
                    </h2>
                    <p className="text-white/40 text-xs text-center uppercase tracking-widest mb-6 font-medium">
                        Stream Mode Restriction
                    </p>

                    {/* Message */}
                    <div
                        className="rounded-xl p-4 mb-6 border border-orange-500/20 text-center"
                        style={{ background: 'rgba(249,115,22,0.08)' }}
                    >
                        <p className="text-white/80 text-sm leading-relaxed">
                            Export is not available while connected in{' '}
                            <span className="text-orange-300 font-bold">Stream Mode</span>.
                            <br />
                            Please switch to <span className="text-indigo-300 font-bold">API Mode</span> to use this feature.
                        </p>
                    </div>

                    {/* Mode comparison */}
                    <div className="grid grid-cols-2 gap-3 mb-6">
                        {/* Stream mode — current, blocked */}
                        <div
                            className="rounded-xl p-3 border border-green-500/20 relative overflow-hidden"
                            style={{ background: 'rgba(34,197,94,0.06)' }}
                        >
                            <div className="absolute top-2 right-2">
                                <span className="text-[9px] font-bold bg-green-500/20 text-green-300 px-1.5 py-0.5 rounded-full border border-green-500/30">
                                    CURRENT
                                </span>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                                <span className="text-green-300 text-xs font-bold">Stream Mode</span>
                            </div>
                            <p className="text-white/40 text-[11px] leading-relaxed">
                                Real-time data via WebSocket. Export not supported.
                            </p>
                            <div className="mt-2 flex items-center gap-1">
                                <WifiOff className="w-3 h-3 text-orange-400" />
                                <span className="text-orange-400 text-[10px] font-medium">Export blocked</span>
                            </div>
                        </div>

                        {/* API mode — required */}
                        <div
                            className="rounded-xl p-3 border border-indigo-500/30 relative overflow-hidden"
                            style={{ background: 'rgba(99,102,241,0.08)' }}
                        >
                            <div className="absolute top-2 right-2">
                                <span className="text-[9px] font-bold bg-indigo-500/20 text-indigo-300 px-1.5 py-0.5 rounded-full border border-indigo-500/30">
                                    REQUIRED
                                </span>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-2 h-2 rounded-full bg-indigo-400" />
                                <span className="text-indigo-300 text-xs font-bold">API Mode</span>
                            </div>
                            <p className="text-white/40 text-[11px] leading-relaxed">
                                REST API connection. Supports full export.
                            </p>
                            <div className="mt-2 flex items-center gap-1">
                                <Download className="w-3 h-3 text-green-400" />
                                <span className="text-green-400 text-[10px] font-medium">Export available</span>
                            </div>
                        </div>
                    </div>

                    {/* How to switch hint */}
                    <div
                        className="rounded-xl p-3 border border-white/8 flex items-start gap-3 mb-6"
                        style={{ background: 'rgba(255,255,255,0.04)' }}
                    >
                        <ArrowRightLeft className="w-4 h-4 text-indigo-400 mt-0.5 shrink-0" />
                        <p className="text-white/50 text-xs leading-relaxed">
                            To switch modes, go back to the <span className="text-white/80 font-medium">dashboard</span> and toggle the connection mode from <span className="text-green-300 font-medium">Stream</span> to <span className="text-indigo-300 font-medium">API</span>.
                        </p>
                    </div>

                    {/* Dismiss button */}
                    <button
                        onClick={onClose}
                        className="w-full py-3 rounded-xl font-semibold text-sm text-white transition hover:opacity-90 active:scale-[0.98]"
                        style={{ background: 'linear-gradient(135deg, #f97316, #eab308)' }}
                    >
                        Got it
                    </button>
                </div>
            </div>
        </div>
    );
}